import { Buffer } from 'buffer';
// Setup Buffer polyfill FIRST (required by @ton/core)
globalThis.Buffer = Buffer;

// Initialize Sentry early
import { initSentry } from '@playproject/webapp-shared';
initSentry();

// Detect if running inside Telegram
function isTelegramWebApp(): boolean {
  try {
    // Check for Telegram WebApp init data in URL hash or window
    return !!(
      window.Telegram?.WebApp ||
      window.location.hash.includes('tgWebAppData') ||
      navigator.userAgent.includes('Telegram')
    );
  } catch {
    return false;
  }
}

// Dynamic import to ensure Buffer is available before any @ton/* modules load
async function main() {
  const [{ StrictMode }, { createRoot }, { ErrorBoundary }] = await Promise.all([
    import('react'),
    import('react-dom/client'),
    import('./components/ErrorBoundary'),
  ]);
  await import('./index.css');

  const root = createRoot(document.getElementById('root')!);

  // Initialize Telegram WebApp SDK only if inside Telegram
  try {
    const WebApp = (await import('@twa-dev/sdk')).default;
    WebApp.ready();
  } catch (err) {
    if (!isTelegramWebApp()) {
      console.warn('[SKYLRK] Not running inside Telegram. Some features may be limited.');
    } else {
      console.error('[SKYLRK] Failed to initialize Telegram SDK:', err);
    }
  }

  const [{ DynamicContextProvider }, { default: App }] = await Promise.all([
    import('@dynamic-labs/sdk-react-core'),
    import('./App'),
  ]);

  // Import TON wallet connectors for Dynamic
  const { TonWalletConnectors } = await import('@dynamic-labs/ton');

  const dynamicEnvId = import.meta.env.VITE_DYNAMIC_ENV_ID || '';

  // TonConnect fallback: keep the provider available for shared hooks that still import it.
  // Once all TonConnect usage is migrated to the Dynamic wallet abstraction, this can be removed.
  const { TonConnectUIProvider } = await import('@tonconnect/ui-react');
  const manifestUrl =
    import.meta.env.VITE_TONCONNECT_MANIFEST_URL ||
    (window.location.hostname !== 'localhost'
      ? `${window.location.origin}/tonconnect-manifest.json`
      : 'https://ton.vote/tonconnect-manifest.json');

  root.render(
    <StrictMode>
      <ErrorBoundary>
        <DynamicContextProvider
          settings={{
            environmentId: dynamicEnvId,
            walletConnectors: [TonWalletConnectors],
          }}
        >
          <TonConnectUIProvider manifestUrl={manifestUrl}>
            <App />
          </TonConnectUIProvider>
        </DynamicContextProvider>
      </ErrorBoundary>
    </StrictMode>
  );
}

main().catch((err) => {
  console.error('[SKYLRK] Fatal error during initialization:', err);
  const root = document.getElementById('root');
  if (root) {
    root.innerHTML = `
      <div style="min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:2rem;background:#1A1A1A;color:#e0e0e0;font-family:'Inter',sans-serif;text-align:center">
        <div style="font-size:3rem;margin-bottom:1rem"></div>
        <h1 style="font-size:1.5rem;font-weight:700;color:#fff;letter-spacing:0.2em">S K Y L R K</h1>
        <p style="color:#a0a0b0;max-width:400px;margin-top:0.75rem">
          ${
            err?.message?.includes('WebApp') || !window.Telegram?.WebApp
              ? 'This app is designed to run inside <strong style="color:#29a9ea">Telegram</strong>. Open it from the Telegram Mini App.'
              : 'Something went wrong. Please try again later.'
          }
        </p>
        <button onclick="location.reload()" style="margin-top:2rem;padding:0.75rem 2rem;border-radius:0.5rem;border:none;background:#3b82f6;color:#fff;font-size:0.875rem;font-weight:600;cursor:pointer">Retry</button>
      </div>
    `;
  }
});
